(() => {
  const ROOT_SELECTOR = '[data-jrb-build-specs]';

  class JrbBuildSpecs {
    constructor(root) {
      this.root = root;
      this.navLinks = Array.from(root.querySelectorAll('[data-build-specs-nav]'));
      this.categories = Array.from(root.querySelectorAll('[data-build-specs-category]'));
      this.overviewAnchor = root.querySelector('.jrb-build-specs__anchor');
      this.openFirst = root.dataset.openFirst === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.pendingNavTargetId = null;
      this.pendingNavTimer = null;
      this.scrollTicking = false;
      this.handleNavClick = this.handleNavClick.bind(this);
      this.handleToggleClick = this.handleToggleClick.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
    }

    init() {
      if (this.root.dataset.jrbBuildSpecsInitialized === 'true') {
        return;
      }

      this.root.dataset.jrbBuildSpecsInitialized = 'true';
      this.root.classList.add('is-enhanced');

      this.navLinks.forEach((link) => {
        link.addEventListener('click', this.handleNavClick);
      });

      this.categories.forEach((category, index) => {
        const toggle = category.querySelector('[data-build-specs-toggle]');
        const panel = category.querySelector('[data-build-specs-panel]');

        if (!toggle || !panel) {
          return;
        }

        toggle.addEventListener('click', this.handleToggleClick);
        this.setExpanded(category, this.openFirst ? index === 0 : false);
      });
      this.setupScrollTracking();
      this.syncActiveNavFromScroll();
    }

    destroy() {
      this.navLinks.forEach((link) => {
        link.removeEventListener('click', this.handleNavClick);
      });

      this.categories.forEach((category) => {
        const toggle = category.querySelector('[data-build-specs-toggle]');

        if (toggle) {
          toggle.removeEventListener('click', this.handleToggleClick);
        }
      });

      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);

      this.clearPendingNavTarget();

      this.root.classList.remove('is-enhanced');
      this.root.dataset.jrbBuildSpecsInitialized = 'false';
    }

    handleNavClick(event) {
      const link = event.currentTarget;
      const targetId = link.dataset.target;
      const target = targetId ? document.getElementById(targetId) : null;

      if (!target) {
        return;
      }

      event.preventDefault();

      this.setActiveNavLink(link);

      if (target.hasAttribute('data-build-specs-category')) {
        this.expandCategory(target);
      }

      this.setPendingNavTarget(target.id);
      this.updateActiveNav(target.id);

      target.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    handleToggleClick(event) {
      const toggle = event.currentTarget;
      const category = toggle.closest('[data-build-specs-category]');

      if (!category) {
        return;
      }

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        this.setExpanded(category, false);
        if (this.overviewAnchor) {
          this.updateActiveNav(this.overviewAnchor.id);
        }
      } else {
        this.expandCategory(category);
      }
    }

    expandCategory(category) {
      this.categories.forEach((item) => {
        this.setExpanded(item, item === category);
      });

      this.updateActiveNav(category.id);
    }

    setPendingNavTarget(targetId) {
      this.clearPendingNavTarget();
      this.pendingNavTargetId = targetId;
      this.pendingNavTimer = window.setTimeout(() => {
        this.clearPendingNavTarget();
      }, this.reducedMotion ? 300 : 1200);
    }

    clearPendingNavTarget() {
      if (this.pendingNavTimer) {
        window.clearTimeout(this.pendingNavTimer);
        this.pendingNavTimer = null;
      }

      this.pendingNavTargetId = null;
    }

    setExpanded(category, expanded) {
      const toggle = category.querySelector('[data-build-specs-toggle]');
      const panel = category.querySelector('[data-build-specs-panel]');
      const state = category.querySelector('[data-build-specs-state]');

      if (!toggle || !panel) {
        return;
      }

      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      panel.hidden = !expanded;
      category.classList.toggle('is-open', expanded);

      if (state) {
        state.textContent = expanded ? 'Collapse' : 'Expand';
      }
    }

    setActiveNavLink(activeLink) {
      this.navLinks.forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    updateActiveNav(activeId) {
      const activeLink = this.navLinks.find((link) => link.dataset.target === activeId);

      if (!activeLink) {
        return;
      }

      this.setActiveNavLink(activeLink);
    }

    setupScrollTracking() {
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange);
    }

    handleViewportChange() {
      if (this.scrollTicking) {
        return;
      }

      this.scrollTicking = true;

      window.requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.syncActiveNavFromScroll();
      });
    }

    syncActiveNavFromScroll() {
      const targets = [this.overviewAnchor].concat(this.categories).filter(Boolean);

      if (!targets.length) {
        return;
      }

      const stickyOffset = parseInt(getComputedStyle(this.root).getPropertyValue('--jrb-build-specs-sticky-top'), 10) || 0;
      const activationOffset = stickyOffset + 44;

      if (this.pendingNavTargetId) {
        const pendingTarget = document.getElementById(this.pendingNavTargetId);

        if (pendingTarget) {
          const pendingTop = pendingTarget.getBoundingClientRect().top;

          if (pendingTop <= activationOffset) {
            this.clearPendingNavTarget();
          } else {
            this.updateActiveNav(this.pendingNavTargetId);
            return;
          }
        } else {
          this.clearPendingNavTarget();
        }
      }

      let activeTarget = targets[0];

      targets.forEach((target) => {
        if (target.getBoundingClientRect().top <= activationOffset) {
          activeTarget = target;
        }
      });

      this.updateActiveNav(activeTarget.id);
    }
  }

  const mount = (scope) => {
    const roots = scope instanceof Element
      ? scope.matches(ROOT_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll(ROOT_SELECTOR))
      : Array.from(document.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (!root._jrbBuildSpecs) {
        root._jrbBuildSpecs = new JrbBuildSpecs(root);
      }

      root._jrbBuildSpecs.init();
    });
  };

  const unmount = (scope) => {
    if (!(scope instanceof Element)) {
      return;
    }

    const roots = scope.matches(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root._jrbBuildSpecs) {
        root._jrbBuildSpecs.destroy();
        delete root._jrbBuildSpecs;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document));
  } else {
    mount(document);
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
